import { LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { SELECTORS } from '../../constants/selectors';
import { isMobileDevice } from './product.logic';

/** TourGuide cookie の max-age（399日 = 34,992,000秒） */
const TOUR_COOKIE_MAX_AGE = 34992000;

/** DOM 描画待ちのディレイ（ms） */
const RENDER_DELAY_MS = 500;

/** AnyGift 要素検出の待ちディレイ（ms） */
const DETECT_DELAY_MS = 100;

/**
 * AnyGift 初期化コンポーネント
 *
 * @element kd-any-gift-initializer
 * @description AnyGift ボタンを適切な場所に挿入し、TourGuide を初期化する。
 */
@customElement('kd-any-gift-initializer')
export class KdAnyGiftInitializer extends LitElement {
  createRenderRoot() {
    return this;
  }

  private hasInitialized = false;

  connectedCallback(): void {
    super.connectedCallback();
    if (!this.hasInitialized) {
      this.hasInitialized = true;
      this.initAnyGift(10);
    }
  }

  /**
   * AnyGift機能を初期化
   * AnyGift要素を検出し、適切なボタンの後に挿入、TourGuideを表示
   */
  private initAnyGift(retryCount: number): void {
    if (retryCount < 1) {
      return;
    }

    setTimeout(() => {
      const anyGiftElement = document.querySelector(SELECTORS.ANY_GIFT.PRODUCT_PAGE_UI);
      if (!anyGiftElement) {
        this.initAnyGift(retryCount - 1);
        return;
      }

      const targetButton = this.findTargetButton();
      if (!targetButton) {
        return;
      }

      this.insertAnyGiftElement(anyGiftElement, targetButton);
    }, DETECT_DELAY_MS);
  }

  /**
   * AnyGiftボタンを挿入するターゲットボタンを検索
   */
  private findTargetButton(): HTMLElement | null {
    if (isMobileDevice(navigator.userAgent)) {
      const productFooterCartButton = document.getElementById(
        SELECTORS.ANY_GIFT.FOOTER_CART_BUTTON_ID
      );
      if (productFooterCartButton) {
        const productVariantSpProductInfoFooter = document.getElementById(
          SELECTORS.ANY_GIFT.SP_PRODUCT_INFO_FOOTER_ID
        );
        if (productVariantSpProductInfoFooter) {
          const rect = productVariantSpProductInfoFooter.getBoundingClientRect();
          const isInView = rect.top > 0 && rect.bottom < window.innerHeight;
          if (isInView) {
            return productVariantSpProductInfoFooter;
          }
        }
        return productFooterCartButton;
      }
    } else {
      const productSideCartButton = document.getElementById(
        SELECTORS.ANY_GIFT.SIDE_CART_BUTTON_ID
      );
      if (productSideCartButton) {
        return productSideCartButton;
      }
    }

    return null;
  }

  /**
   * AnyGift要素をターゲットボタンの後に挿入し、TourGuideを初期化
   */
  private async insertAnyGiftElement(
    anyGiftElement: Element,
    targetButton: HTMLElement
  ): Promise<void> {
    try {
      const isTourWrapper = document.querySelector(SELECTORS.ANY_GIFT.TOUR_WRAPPER);
      const hasTourCookie = document.cookie.includes('tour_anygift=true');

      if (!isTourWrapper && !hasTourCookie) {
        const tourWrapper = document.createElement('div');
        tourWrapper.setAttribute(
          'data-tg-tour',
          'Kuradashi e-gift対象商品です。カードのデザインを選択、メッセージを入力し、購入後のURLをLINEやSNSで送ることができます。'
        );
        tourWrapper.appendChild(anyGiftElement);
        targetButton.parentNode?.insertBefore(tourWrapper, targetButton.nextSibling);

        await this.initTourGuide();

        document.cookie = `tour_anygift=true; path=/; max-age=${TOUR_COOKIE_MAX_AGE}`;
      } else if (isTourWrapper) {
        targetButton.parentNode?.insertBefore(isTourWrapper, targetButton.nextSibling);
      } else {
        targetButton.parentNode?.insertBefore(anyGiftElement, targetButton.nextSibling);
      }
    } catch (error) {
      console.error('[kd-any-gift-initializer] AnyGiftボタンの挿入に失敗:', error);
    }
  }

  /**
   * TourGuideを動的importして初期化
   */
  private async initTourGuide(): Promise<void> {
    await new Promise((resolve) => {
      setTimeout(resolve, RENDER_DELAY_MS);
    });

    try {
      const { TourGuideClient } = await import('@sjmc11/tourguidejs');
      const tg = new TourGuideClient({
        autoScrollSmooth: true,
        showButtons: false,
        showStepDots: false,
        rememberStep: false,
        showStepProgress: false,
      });
      tg.start();
    } catch (error) {
      console.error('[kd-any-gift-initializer] TourGuide初期化に失敗:', error);
    }
  }

  render() {
    return nothing;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-any-gift-initializer': KdAnyGiftInitializer;
  }
}
